import Grid from "@bmi-digital/components/grid";
import IconList from "@bmi-digital/components/icon-list";
import CheckIcon from "@bmi-digital/components/icon/Check";
import LeadBlock from "@bmi-digital/components/lead-block";
import Section from "@bmi-digital/components/section";
import Typography from "@bmi-digital/components/typography";
import { replaceSpaces } from "@bmi-digital/components/utils";
import { microCopy } from "@bmi-digital/dxb-microcopies";
import { styled, useTheme } from "@mui/material/styles";
import { useLocation } from "@reach/router";
import { graphql } from "gatsby";
import queryString from "query-string";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from "react";
import Breadcrumbs from "../../../components/Breadcrumbs";
import FiltersSidebar from "../../../components/FiltersSidebar";
import Page from "../../../components/Page";
import ProgressIndicator from "../../../components/ProgressIndicator";
import ResultsPagination from "../../../components/ResultsPagination";
import RichText, { RichTextData } from "../../../components/RichText";
import Scrim from "../../../components/Scrim";
import { useSiteContext } from "../../../components/Site";
import Link from "../../../components/link/Link";
import { useConfig } from "../../../contexts/ConfigProvider";
import { updateBreadcrumbTitleFromPayload } from "../../../utils/breadcrumbUtils";
import { devLog } from "../../../utils/devLog";
import BackToResults from "../../../components/BackToResults";
import {
  compileElasticSearchQuery,
  disableFiltersFromAggregations,
  queryElasticSearch
} from "../../../utils/elasticSearch";
import { xferFilterValue } from "../../../utils/elasticSearchPLP";
import {
  clearFilterValues,
  convertToURLFilters,
  replaceDotFiltersParameter,
  updateFilterValue
} from "../../../utils/filters";
import {
  generateHeroLevel,
  generateHeroProps
} from "../../../utils/heroLevelUtils";
import { renderHero } from "../../../utils/heroTypesUI";
import { removePLPFilterPrefix } from "../../../utils/product-filters";
import { ProductListGrid, ProductListWrapperGrid } from "../styles";
import {
  renderProducts,
  resolveFilters
} from "../utils/productListerPageUtils";
import { PayloadProductListerPage } from "../../../types/payload";
import type { URLProductFilter } from "../../../utils/filters";
import type { HomePageData, SiteData } from "../../../components/Site";
import type { PageData as PageData } from "../../../components/Page";
import type { SpotlightHeroProps } from "@bmi-digital/components/spotlight-hero";
import type { HeroProps } from "@bmi-digital/components/hero";
import type { ProductListerPageInfoHero } from "../../../components/PageInfo";
import type { PLPFilterResponse, ProductFilter } from "../../../types/pim";
import type { LinkData } from "../../../components/link/types";
import type { MicroCopy as MicroCopyData } from "../../../components/MicroCopy";
import type { CalculatorConfig } from "../../../components/pitched-roof-calculator/types";
import type { HouseType } from "../../../components/visualiser/Types";
import type { GeneralErrorData } from "../../general-error";
import type {
  CountryNavigation,
  SampleOrder
} from "../../../components/Header";
import type { Visualiser } from "../../../components/Visualiser";
import type { BreadcrumbItem } from "../../../types/breadcrumb";

const PAGE_SIZE = 24;
const ES_INDEX_NAME = process.env.GATSBY_ES_INDEX_NAME_PRODUCTS;

export type ProductListerPageData = PageData &
  ProductListerPageInfoHero &
  Pick<
    PayloadProductListerPage,
    "breadcrumbTitle" | "features" | "heroType" | "allowFilterBy"
  > & {
    content: RichTextData;
    featuresLink: readonly LinkData[] | null;
    cta: readonly LinkData[] | null;
  };

type QueryParams = {
  filters: URLProductFilter[];
};

export type Props = {
  pageContext: {
    categoryCodes: string[];
    allowFilterBy: string[];
    variantCodeToPathMap?: Record<string, string>;
  };
  data: {
    payloadProductListerPage: ProductListerPageData;
    plpFilters: PLPFilterResponse;
    payloadSite: SiteData;
    allPayloadMicroCopy: {
      nodes: MicroCopyData[];
    };
    payloadPitchedRoofCalculatorConfig: CalculatorConfig | null;
    allPayloadHouseModel: {
      nodes: HouseType[];
    };
    payloadHomePage: HomePageData;
    payloadGeneralError: GeneralErrorData;
    payloadCountryNavigation: CountryNavigation;
    payloadSampleOrder: SampleOrder | null;
    payloadVisualiser: Visualiser | null;
  };
};

const StyledBlueCheckIcon = styled(CheckIcon)(({ theme }) => ({
  color: theme.colours.inter
}));

const ProductListerPageContent = ({
  productListerPageData,
  enhancedBreadcrumbs,
  plpFilters,
  categoryCodes,
  variantCodeToPathMap
}: {
  productListerPageData: ProductListerPageData;
  enhancedBreadcrumbs: BreadcrumbItem[];
  plpFilters: PLPFilterResponse;
  categoryCodes: Props["pageContext"]["categoryCodes"];
  variantCodeToPathMap: Props["pageContext"]["variantCodeToPathMap"];
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const { isPreviewMode, isBrandProviderEnabled } = useConfig();
  const { getMicroCopy } = useSiteContext();
  const resolvedFilters = useMemo(() => {
    return resolveFilters(plpFilters?.filters);
  }, [plpFilters]);
  const [filters, setFilters] = useState(resolvedFilters);
  const resultsElement = useRef<HTMLDivElement>(null);
  const [products, setProducts] = useState([]);
  const [page, setPage] = useState(0);
  const [pageCount, setPageCount] = useState(
    Math.ceil(products.length / PAGE_SIZE)
  );
  const siteContext = useSiteContext();
  const location = useLocation();
  const theme = useTheme();

  const queryParams = useMemo<QueryParams>(() => {
    const parsedQueryParams = queryString.parse(location.search);
    return {
      ...parsedQueryParams,
      ...(parsedQueryParams.filters
        ? {
            filters: JSON.parse(
              replaceDotFiltersParameter(parsedQueryParams.filters as string)
            )
          }
        : { filters: [] })
    };
  }, [location]);

  useEffect(() => {
    if (queryParams?.filters?.length) {
      // Filter search
      const updatedFilters = filters.map((filter) => {
        const currentQueryFilterValue = queryParams.filters.find(
          // TODO: DXB-3449 - remove toUpperCase when PIM has completed BPN-1055
          ({ name }) =>
            name.toUpperCase() ===
            removePLPFilterPrefix(filter.name).toUpperCase()
        )?.value;

        return {
          ...filter,
          value: [].concat(currentQueryFilterValue).filter(Boolean)
        };
      });

      setFilters(updatedFilters);
      fetchProducts(updatedFilters, categoryCodes, 0, PAGE_SIZE);
    } else {
      //wait for allow filter by to be resolved before starting a seach
      if (plpFilters && plpFilters.allowFilterBy) {
        // Default search (no filters)
        setFilters(resolvedFilters);
        fetchProducts(resolvedFilters, categoryCodes, 0, PAGE_SIZE);
      } else {
        devLog("could not resolve 'data.plpFilters'");
      }
    }
  }, [location.search, categoryCodes, queryParams.filters, resolvedFilters]);

  const heroLevel = generateHeroLevel(
    productListerPageData.heroType,
    enhancedBreadcrumbs
  );

  const heroProps: HeroProps | SpotlightHeroProps = generateHeroProps(
    productListerPageData.title,
    heroLevel,
    productListerPageData.subtitle,
    productListerPageData.featuredVideo,
    productListerPageData.featuredMedia,
    productListerPageData.cta && productListerPageData.cta[0],
    theme
  );

  const isDarkThemed =
    productListerPageData.heroType === "Spotlight" || heroLevel !== 3;
  const breadcrumbsNode = (
    <BackToResults isDarkThemed={isDarkThemed}>
      <Breadcrumbs
        data={enhancedBreadcrumbs}
        isDarkThemed={isDarkThemed}
        data-testid="product-lister-page-breadcrumbs-top"
      />
    </BackToResults>
  );

  const isHeroKeyLine = Boolean(
    isBrandProviderEnabled && productListerPageData.brandLogo
  );
  const features = productListerPageData.features;
  const featuresLink = productListerPageData.featuresLink;
  const isFeaturesArrayExist = features && features.length > 0;
  const isKeyFeatureBlockVisible =
    isFeaturesArrayExist || (featuresLink && featuresLink.length > 0);

  const fetchProducts = useCallback(
    async (filters, categoryCodes, page, pageSize) => {
      if (isLoading) {
        devLog("Already loading...");
        return;
      }

      if (isPreviewMode) {
        alert("You cannot search on the preview environment.");
        return;
      }

      setIsLoading(true);

      const query = compileElasticSearchQuery({
        allowFilterBy: plpFilters.allowFilterBy,
        categoryCodes,
        filters, //these are updated filters with user's selection from UI!
        groupByVariant: process.env.GATSBY_GROUP_BY_VARIANT === "true",
        page,
        pageSize
      });

      // TODO: If no query returned, empty query, show default results?
      // TODO: Handle if no response
      const results = await queryElasticSearch(query, ES_INDEX_NAME);

      if (results && results.hits) {
        const { hits } = results;
        const uniqueBaseProductsCount =
          results.aggregations?.unique_base_products_count?.value || 0;
        const newPageCount = Math.ceil(uniqueBaseProductsCount / PAGE_SIZE);
        const variants = (() => {
          if ((page + 1) * PAGE_SIZE > uniqueBaseProductsCount) {
            return hits.hits.slice(
              0,
              uniqueBaseProductsCount - page * PAGE_SIZE
            );
          }
          return hits.hits;
        })();

        setPageCount(newPageCount);
        setPage(newPageCount < page ? 0 : page);
        setProducts(
          variants.map((hit) => ({
            ...hit._source,
            all_variants: hit.inner_hits.all_variants.hits.hits || []
          }))
        );
      }

      if (results && results.aggregations) {
        // TODO: Remove 'GATSBY_USE_LEGACY_FILTERS' branch of code
        // JIRA : https://bmigroup.atlassian.net/browse/DXB-2789
        const newFilters = disableFiltersFromAggregations(
          filters,
          results.aggregations
        );

        setFilters(newFilters);
      }

      setIsLoading(false);

      return results;
    },
    [plpFilters.allowFilterBy, isLoading, isPreviewMode]
  );

  const onFiltersChange = async (newFilters: ProductFilter[]) => {
    // NOTE: If filters change, we reset pagination to first page
    const result = await fetchProducts(newFilters, categoryCodes, 0, PAGE_SIZE);

    // TODO: Remove 'GATSBY_USE_LEGACY_FILTERS' branch of code
    // JIRA : https://bmigroup.atlassian.net/browse/DXB-2789
    if (result && result.aggregations) {
      setFilters(
        xferFilterValue(
          newFilters,
          disableFiltersFromAggregations(filters, result.aggregations)
        )
      );

      return;
    }

    setFilters(newFilters);
  };

  const handleFiltersChange = (
    filterName: string,
    filterValue: string,
    checked: boolean
  ) => {
    const newFilters: ProductFilter[] = updateFilterValue(
      filters,
      filterName,
      filterValue,
      checked
    );
    const URLFilters = convertToURLFilters(newFilters);

    history.replaceState(
      null,
      "",
      `${location.pathname}?${queryString.stringify({
        filters: JSON.stringify(URLFilters)
      })}`
    );

    onFiltersChange(newFilters);
  };

  // Resets all selected filter values to nothing
  const handleClearFilters = () => {
    history.replaceState(null, "", location.pathname);
    const newFilters = clearFilterValues(filters);
    onFiltersChange(newFilters);
  };

  const handlePageChange = async (
    _: React.ChangeEvent<unknown>,
    page: number
  ) => {
    await fetchProducts(filters, categoryCodes, page - 1, PAGE_SIZE);
    const scrollY = resultsElement.current
      ? resultsElement.current.offsetTop - 200
      : 0;
    window.scrollTo(0, scrollY);
  };

  return (
    <>
      {isLoading ? (
        <Scrim theme="light">
          <ProgressIndicator theme="light" />
        </Scrim>
      ) : null}
      {renderHero(heroProps, breadcrumbsNode, productListerPageData.heroType, {
        isHeroKeyLine: isHeroKeyLine,
        isSpotlightHeroKeyLine: isHeroKeyLine
      })}
      <Section
        backgroundColor="white"
        data-testid={`product-lister-page-lead-block-section`}
      >
        <LeadBlock>
          <LeadBlock.Content>
            <RichText
              document={productListerPageData.content}
              underlineHeadings={["h2", "h3", "h4"]}
            />
          </LeadBlock.Content>
          {isKeyFeatureBlockVisible ? (
            <LeadBlock.Card
              color="pearl"
              data-testid="key-features-post-it-card"
            >
              <LeadBlock.Card.Section>
                <LeadBlock.Card.Heading hasUnderline>
                  {getMicroCopy(microCopy.PLP_KEY_FEATURES_TITLE)}
                </LeadBlock.Card.Heading>
                <LeadBlock.Card.Content>
                  {isFeaturesArrayExist && (
                    <IconList>
                      {features?.map((feature, index) => (
                        <IconList.Item
                          key={index}
                          icon={<StyledBlueCheckIcon />}
                          title={feature}
                          isCompact
                        />
                      ))}
                    </IconList>
                  )}
                  {featuresLink && featuresLink[0] && (
                    <Link
                      data={featuresLink[0]}
                      gtm={{
                        id: "cta-click1",
                        label: `${getMicroCopy(
                          microCopy.PLP_KEY_FEATURES_TITLE
                        )} - ${featuresLink[0].label}`
                      }}
                    >
                      {featuresLink[0].label}
                    </Link>
                  )}
                </LeadBlock.Card.Content>
              </LeadBlock.Card.Section>
            </LeadBlock.Card>
          ) : null}
        </LeadBlock>
      </Section>
      <Section
        backgroundColor="pearl"
        overflowVisible
        data-testid={`product-lister-page-section-${replaceSpaces(
          productListerPageData.title
        )}`}
      >
        <Grid container spacing={3}>
          {filters && filters.length ? (
            <Grid xs={12} md={12} lg={3}>
              <FiltersSidebar
                filters={filters}
                onFiltersChange={handleFiltersChange}
                onClearFilters={handleClearFilters}
              />
            </Grid>
          ) : null}
          <ProductListGrid
            xs={12}
            md={12}
            lg={filters.length ? 9 : 12}
            ref={resultsElement}
          >
            <ProductListWrapperGrid container spacing={3}>
              {!isLoading && products.length === 0 && (
                <Typography>
                  {getMicroCopy(microCopy.PLP_PRODUCT_NO_RESULTS_FOUND)}
                </Typography>
              )}
              {renderProducts(
                products,
                variantCodeToPathMap,
                siteContext.countryCode,
                getMicroCopy,
                filters
              )}
            </ProductListWrapperGrid>
            <ResultsPagination
              page={page + 1}
              onPageChange={handlePageChange}
              count={pageCount}
            />
          </ProductListGrid>
        </Grid>
      </Section>
      <Section
        backgroundColor="alabaster"
        isSlim
        data-testid={`product-lister-page-breadcrumb-bottom`}
      >
        <Breadcrumbs
          data={enhancedBreadcrumbs}
          data-testid="product-lister-page-breadcrumbs-bottom"
        />
      </Section>
    </>
  );
};

const ProductListerPage = ({ pageContext, data }: Props) => {
  const { title, brandLogo, featuredMedia, signupBlock, meta } =
    data.payloadProductListerPage;

  const enhancedBreadcrumbs = updateBreadcrumbTitleFromPayload(
    data.payloadProductListerPage.breadcrumbs,
    data.payloadProductListerPage.breadcrumbTitle
  );

  const pageData: PageData = {
    breadcrumbs: enhancedBreadcrumbs,
    signupBlock,
    meta,
    path: data.payloadProductListerPage.path
  };

  return (
    <Page
      brand={brandLogo}
      title={title}
      pageData={pageData}
      siteData={data.payloadSite}
      microCopy={data.allPayloadMicroCopy.nodes}
      pitchedRoofCalculatorConfig={data.payloadPitchedRoofCalculatorConfig}
      visualiserHouseTypes={data.allPayloadHouseModel.nodes}
      homePage={data.payloadHomePage}
      generalError={data.payloadGeneralError}
      countryNavigation={data.payloadCountryNavigation}
      sampleOrder={data.payloadSampleOrder}
      visualiser={data.payloadVisualiser}
      variantCodeToPathMap={pageContext?.variantCodeToPathMap}
      ogImageUrl={featuredMedia?.sizes.heroDesktop.url}
    >
      <ProductListerPageContent
        productListerPageData={data.payloadProductListerPage}
        enhancedBreadcrumbs={enhancedBreadcrumbs}
        plpFilters={data.plpFilters}
        categoryCodes={pageContext.categoryCodes}
        variantCodeToPathMap={pageContext.variantCodeToPathMap}
      />
    </Page>
  );
};

export default ProductListerPage;

export const pageQuery = graphql`
  query ProductListerPageById(
    $pageId: String!
    $categoryCodes: [String!]
    $allowFilterBy: [String!]
  ) {
    payloadProductListerPage(id: { eq: $pageId }) {
      ...PageInfoHeroFragment
      ...PayloadProductListerPageFragment
      breadcrumbTitle
      content {
        ...RichTextFragment
      }
      features
      featuresLink {
        ...LinkFragment
      }
      heroType
      cta {
        ...LinkFragment
      }
      allowFilterBy
    }
    plpFilters(categoryCodes: $categoryCodes, allowFilterBy: $allowFilterBy) {
      filters {
        filterCode
        label
        name
        options {
          label
          value
        }
      }
      allowFilterBy
    }
    payloadSite {
      ...SiteFragment
    }
    allPayloadMicroCopy {
      nodes {
        ...MicroCopyFragment
      }
    }
    payloadPitchedRoofCalculatorConfig {
      ...PitchedRoofCalculatorFragment
    }
    allPayloadHouseModel {
      nodes {
        ...VisualiserHouseFragment
      }
    }
    payloadHomePage {
      ...HomePageFragment
    }
    payloadGeneralError {
      ...GeneralErrorFragment
    }
    payloadCountryNavigation {
      ...CountryNavigationFragment
    }
    payloadSampleOrder {
      ...SampleOrderFragment
    }
    payloadVisualiser {
      ...VisualiserFragment
    }
  }
`;
